/**
 * Data Provider Helpers
 * 
 * Utility functions to make migrating stores to dataProvider easier.
 * These helpers bridge the gap between the old Supabase API and the new DataProvider interface.
 */

import { getDataProvider, isStandaloneMode } from './index'
import type { QueryOptions, QueryFilter, OperationResult, ChangePayload, Subscription } from './types'
import { supabase } from '@/lib/supabase'
import { logger } from '@/lib/logger'

/**
 * Get the current user ID from the appropriate source
 * In online mode, uses Supabase auth
 * In standalone mode, uses the standalone user
 */
export function getCurrentUserId(): string | null {
  const provider = getDataProvider()
  return provider.getCurrentUserId()
}

/**
 * Check if the user is authenticated
 */
export function isAuthenticated(): boolean {
  const provider = getDataProvider()
  const state = provider.getAuthState()
  return state.isAuthenticated
}

/**
 * Query helper that works with both modes
 * Returns data in the same format as Supabase for easy migration
 */
export async function query<T>(
  table: string,
  options?: QueryOptions
): Promise<{ data: T[] | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    const data = await provider.query<T>(table, options)
    return { data, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Query error on ${table}:`, err)
    return { data: null, error: err as Error }
  }
}

/**
 * Get by ID helper
 */
export async function getById<T>(
  table: string,
  id: string
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    const data = await provider.getById<T>(table, id)
    return { data, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] GetById error on ${table}:`, err)
    return { data: null, error: err as Error }
  }
}

/**
 * Insert helper that returns data in Supabase format
 */
export async function insert<T>(
  table: string,
  data: Partial<T>,
  userId?: string
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    
    // Add user_id if provided
    const insertData = userId ? { ...data, user_id: userId } : data
    
    const result = await provider.insert<T>(table, insertData)
    
    if (!result.success) {
      return { data: null, error: new Error(result.error) }
    }
    
    return { data: result.data || null, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Insert error on ${table}:`, err)
    return { data: null, error: err as Error }
  }
}

/**
 * Update helper
 */
export async function update<T>(
  table: string,
  id: string,
  data: Partial<T>
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    const result = await provider.update<T>(table, id, data)
    
    if (!result.success) {
      return { data: null, error: new Error(result.error) }
    }
    
    return { data: result.data || null, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Update error on ${table}:`, err)
    return { data: null, error: err as Error }
  }
}

/**
 * Bulk update helper (for operations like "deactivate all scenes")
 */
export async function updateMany<T>(
  table: string,
  filters: QueryFilter[],
  data: Partial<T>
): Promise<{ count: number | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    const result = await provider.updateMany<T>(table, filters, data)
    
    if (!result.success) {
      return { count: null, error: new Error(result.error) }
    }
    
    return { count: result.data || 0, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] UpdateMany error on ${table}:`, err)
    return { count: null, error: err as Error }
  }
}

/**
 * Delete helper
 */
export async function deleteRecord(
  table: string,
  id: string
): Promise<{ error: Error | null }> {
  try {
    const provider = getDataProvider()
    const result = await provider.delete(table, id)
    
    if (!result.success) {
      return { error: new Error(result.error) }
    }
    
    return { error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Delete error on ${table}:`, err)
    return { error: err as Error }
  }
}

/**
 * Upsert helper
 */
export async function upsert<T>(
  table: string,
  data: Partial<T>,
  onConflict?: string
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    const result = await provider.upsert<T>(table, data, onConflict)
    
    if (!result.success) {
      return { data: null, error: new Error(result.error) }
    }
    
    return { data: result.data || null, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Upsert error on ${table}:`, err)
    return { data: null, error: err as Error }
  }
}

/**
 * Subscribe to table changes
 * Works with both Supabase Realtime and BroadcastChannel
 */
export function subscribeToTable<T>(
  table: string,
  callback: (payload: ChangePayload<T>) => void,
  userFilter?: string | { column: string; value: string }
): Subscription {
  const provider = getDataProvider()
  
  let filters: QueryFilter[] = []
  if (userFilter) {
    if (typeof userFilter === 'string') {
      filters = [{ column: 'user_id', operator: 'eq', value: userFilter }]
    } else {
      filters = [{ column: userFilter.column, operator: 'eq', value: userFilter.value }]
    }
  }
  
  return provider.subscribe<T>(table, callback, filters)
}

/**
 * Subscribe to a broadcast channel for custom events
 */
export function subscribeToChannel(
  channelName: string,
  eventHandlers: Record<string, (payload: any) => void>
): Subscription {
  const provider = getDataProvider()
  return provider.subscribeToChannel(channelName, eventHandlers)
}

/**
 * Broadcast an event to a channel
 */
export async function broadcast(
  channel: string,
  event: string,
  payload: any
): Promise<void> {
  const provider = getDataProvider()
  await provider.broadcast(channel, event, payload)
}

/**
 * Upload a file to storage
 */
export async function uploadFile(
  bucket: string,
  path: string,
  file: Blob | File
): Promise<{ path: string | null; error: Error | null }> {
  try {
    const provider = getDataProvider()
    const result = await provider.uploadFile(bucket, path, file)
    
    if (!result.success) {
      return { path: null, error: new Error(result.error) }
    }
    
    return { path: result.data || null, error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Upload error:`, err)
    return { path: null, error: err as Error }
  }
}

/**
 * Get a file URL from storage
 */
export async function getFileUrl(
  bucket: string,
  path: string,
  expiresIn?: number
): Promise<string | null> {
  const provider = getDataProvider()
  return provider.getFileUrl(bucket, path, expiresIn)
}

/**
 * Delete a file from storage
 */
export async function deleteFile(
  bucket: string,
  path: string
): Promise<{ error: Error | null }> {
  try {
    const provider = getDataProvider()
    const result = await provider.deleteFile(bucket, path)
    
    if (!result.success) {
      return { error: new Error(result.error) }
    }
    
    return { error: null }
  } catch (err) {
    logger.error(`[DataProviderHelpers] Delete file error:`, err)
    return { error: err as Error }
  }
}

/**
 * Build query filters from common patterns
 */
export function buildFilters(conditions: Record<string, any>): QueryFilter[] {
  return Object.entries(conditions)
    .filter(([_, value]) => value !== undefined)
    .map(([column, value]) => ({
      column,
      operator: 'eq' as const,
      value
    }))
}

/**
 * Create a filter for user_id = currentUserId
 */
export function userFilter(): QueryFilter | null {
  const userId = getCurrentUserId()
  if (!userId) return null
  
  return { column: 'user_id', operator: 'eq', value: userId }
}
